import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type CostPeriod, type LLMUsageOverview } from "@/lib/api";
import { formatCost, formatCostLong, formatTokens } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// ── Types ────────────────────────────────────────────────────────

type ByContainer = LLMUsageOverview["byContainer"][number];
type ByOrg = LLMUsageOverview["byOrg"][number];
type ByModel = LLMUsageOverview["byModel"][number];

// ── Period selector ─────────────────────────────────────────────

const PERIODS: { value: CostPeriod; label: string }[] = [
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_30d", label: "Last 30 Days" },
  { value: "last_90d", label: "Last 90 Days" },
];

function PeriodSelector({ value, onChange }: { value: CostPeriod; onChange: (p: CostPeriod) => void }) {
  return (
    <div className="flex gap-1">
      {PERIODS.map((p) => (
        <Button
          key={p.value}
          variant={value === p.value ? "default" : "outline"}
          size="sm"
          onClick={() => onChange(p.value)}
        >
          {p.label}
        </Button>
      ))}
    </div>
  );
}

// ── Stat card ───────────────────────────────────────────────────

function StatCard({ title, value, accent }: { title: string; value: string; accent?: string }) {
  return (
    <Card className="py-4">
      <CardHeader className="pb-0">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-2xl font-semibold tabular-nums ${accent ?? ""}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card className="py-4">
      <CardHeader className="pb-0">
        <Skeleton className="h-4 w-24" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-20 mt-1" />
      </CardContent>
    </Card>
  );
}

function TableSkeleton({ cols, rows = 4 }: { cols: number; rows?: number }) {
  return (
    <div className="rounded-md border">
      <div className="divide-y">
        <div className="flex gap-4 px-4 py-3">
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-24" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex gap-4 px-4 py-3">
            {Array.from({ length: cols }).map((_, j) => (
              <Skeleton key={j} className="h-4 w-20" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Column definitions ──────────────────────────────────────────

// `Raw` = what upstream charged us. `Billed` = what we charged the customer.
// Spread is our margin; shown so we can spot orgs running at a loss or
// orgs paying outsized markup vs the rest of the fleet.
function MarginCell({ raw, billed }: { raw: number; billed: number }) {
  if (raw <= 0) return <span className="text-muted-foreground tabular-nums">—</span>;
  const pct = ((billed - raw) / raw) * 100;
  const color = pct < 0 ? "text-destructive" : "text-muted-foreground";
  return <span className={`tabular-nums ${color}`}>{pct >= 0 ? "+" : ""}{pct.toFixed(0)}%</span>;
}

const byContainerColumns: ColumnDef<ByContainer>[] = [
  {
    accessorKey: "containerName",
    header: "Container",
    cell: ({ row }) => (
      <span className="font-medium">{row.original.containerName}</span>
    ),
  },
  {
    accessorKey: "organizationName",
    header: "Organization",
    cell: ({ row }) => {
      const { organizationId, organizationName } = row.original;
      if (!organizationId) return <span className="text-muted-foreground">—</span>;
      return (
        <Link
          to="/superadmin/organizations/$orgId"
          params={{ orgId: organizationId }}
          target="_blank"
          className="text-sm text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
        >
          {organizationName ?? organizationId}
        </Link>
      );
    },
  },
  {
    accessorKey: "rawCostUsd",
    header: "Raw",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "markedUpCostUsd",
    header: "Billed",
    cell: ({ getValue }) => <span className="font-medium tabular-nums">{formatCost(getValue() as number)}</span>,
  },
  {
    id: "margin",
    header: "Margin",
    cell: ({ row }) => <MarginCell raw={row.original.rawCostUsd} billed={row.original.markedUpCostUsd} />,
  },
  {
    accessorKey: "tokens",
    header: "Tokens",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatTokens(getValue() as number)}</span>,
  },
  {
    accessorKey: "calls",
    header: "Calls",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{(getValue() as number).toLocaleString()}</span>,
  },
];

const byOrgColumns: ColumnDef<ByOrg>[] = [
  {
    accessorKey: "organizationName",
    header: "Organization",
    cell: ({ row }) => {
      const { organizationId, organizationName } = row.original;
      if (!organizationId) return <span className="text-muted-foreground">(unknown)</span>;
      return (
        <Link
          to="/superadmin/organizations/$orgId"
          params={{ orgId: organizationId }}
          target="_blank"
          className="text-sm text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
        >
          {organizationName ?? organizationId}
        </Link>
      );
    },
  },
  {
    accessorKey: "rawCostUsd",
    header: "Raw",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "markedUpCostUsd",
    header: "Billed",
    cell: ({ getValue }) => <span className="font-medium tabular-nums">{formatCost(getValue() as number)}</span>,
  },
  {
    id: "margin",
    header: "Margin",
    cell: ({ row }) => <MarginCell raw={row.original.rawCostUsd} billed={row.original.markedUpCostUsd} />,
  },
  {
    accessorKey: "tokens",
    header: "Tokens",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatTokens(getValue() as number)}</span>,
  },
  {
    accessorKey: "calls",
    header: "Calls",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{(getValue() as number).toLocaleString()}</span>,
  },
];

const byModelColumns: ColumnDef<ByModel>[] = [
  {
    accessorKey: "model",
    header: "Model",
    cell: ({ getValue }) => <span className="font-medium">{(getValue() as string | null) ?? "(unknown)"}</span>,
  },
  {
    accessorKey: "provider",
    header: "Provider",
    cell: ({ getValue }) => {
      const v = getValue() as string | null;
      return v ? <Badge variant="outline">{v}</Badge> : <span className="text-muted-foreground">—</span>;
    },
  },
  {
    accessorKey: "rawCostUsd",
    header: "Raw",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "markedUpCostUsd",
    header: "Billed",
    cell: ({ getValue }) => <span className="font-medium tabular-nums">{formatCost(getValue() as number)}</span>,
  },
  {
    accessorKey: "tokens",
    header: "Tokens",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{formatTokens(getValue() as number)}</span>,
  },
  {
    accessorKey: "calls",
    header: "Calls",
    cell: ({ getValue }) => <span className="tabular-nums text-muted-foreground">{(getValue() as number).toLocaleString()}</span>,
  },
];

// ── Page ────────────────────────────────────────────────────────

export function AdminLLMUsagePage() {
  useDocumentTitle("Admin: LLM usage");
  const [period, setPeriod] = useState<CostPeriod>("this_month");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-llm-usage", period],
    queryFn: () => api.admin.llmUsage(period),
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title="LLM Usage" subtitle="Brain LLM spend recorded by boo-gateway." />
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {isLoading ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>
          <TableSkeleton cols={5} />
          <TableSkeleton cols={4} rows={3} />
          <TableSkeleton cols={5} />
        </div>
      ) : isError || !data ? (
        <ErrorComponent error={error ?? "Failed to load LLM usage"} />
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              title="Raw cost"
              value={formatCostLong(data.totals.rawCostUsd)}
            />
            <StatCard
              title="Billed"
              value={formatCostLong(data.totals.markedUpCostUsd)}
              accent="text-primary"
            />
            <StatCard
              title="Margin"
              value={
                data.totals.rawCostUsd > 0
                  ? `${(((data.totals.markedUpCostUsd - data.totals.rawCostUsd) / data.totals.rawCostUsd) * 100).toFixed(0)}%`
                  : "—"
              }
            />
            <StatCard title="Calls" value={data.totals.calls.toLocaleString()} />
            <StatCard title="Tokens" value={formatTokens(data.totals.tokens)} />
          </div>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">By Container</h2>
              <Badge variant="secondary">{data.byContainer.length}</Badge>
            </div>
            {data.byContainer.length > 0 ? (
              <DataTable columns={byContainerColumns} data={data.byContainer} />
            ) : (
              <p className="text-sm text-muted-foreground">No LLM usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">By Organization</h2>
              <Badge variant="secondary">{data.byOrg.length}</Badge>
            </div>
            {data.byOrg.length > 0 ? (
              <DataTable columns={byOrgColumns} data={data.byOrg} />
            ) : (
              <p className="text-sm text-muted-foreground">No LLM usage in this period.</p>
            )}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">By Model</h2>
              <Badge variant="secondary">{data.byModel.length}</Badge>
            </div>
            {data.byModel.length > 0 ? (
              <DataTable columns={byModelColumns} data={data.byModel} />
            ) : (
              <p className="text-sm text-muted-foreground">No LLM usage in this period.</p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
